// BUILD-042 — "Review & Sign" for an equipment checkout (Oct 15 08:13).
//
// STATUS: prepared, NOT CONNECTED. No BoldSign account exists. When the server reports the provider is
// not connected (the local preview), this panel says so in a persistent banner and the signing shown is
// SIMULATED - it is not BoldSign and not a legal signature. When no provider is configured at all, it
// says electronic signing is not set up and points to the existing print / sign / upload path.
//
// The employee signs on the EXISTING signature line of the generated form (the server places the
// provider's fields on the recorded line; this UI never positions anything). New forms have no witness. The witness
// status text and button below appear ONLY for a form issued before witnesses were removed (its all-required-signers rule is kept). A witness signs
// on their OWN line under their OWN identity. Completion is whatever the server verified at the provider
// - this panel never marks anything complete itself.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getHrDocumentSigning, startHrDocumentSigning, syncHrDocumentSigning, cancelHrDocumentSigning,
  type HrDocumentAssignment, type HrSigningStatus,
} from '../../../api/client';

const REQUEST_LABEL: Record<string, string> = {
  prepared: 'Preparing',
  awaiting_signers: 'Waiting for signatures',
  completed: 'Signed by everyone — sent to HR for review',
  cancelled: 'Signing cancelled',
  failed: 'Signing did not finish — you can start again',
};

export function SigningPanel({
  assignment, mode, callerEmployeeId, onChanged,
}: {
  assignment: HrDocumentAssignment;
  /** 'self' = the employee's own page; 'admin' = HR administration for this employee. */
  mode: 'self' | 'admin';
  callerEmployeeId: string | null;
  onChanged: () => void;
}) {
  const [status, setStatus] = useState<HrSigningStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const awaitingProvider = useRef(false);

  const load = useCallback(() => {
    getHrDocumentSigning(assignment.assignmentId)
      .then((s) => { setStatus(s); setError(null); })
      .catch((e: any) => setError(e.message || 'Could not load signing status.'));
  }, [assignment.assignmentId]);
  useEffect(() => { load(); }, [load, assignment.status, assignment.updatedAt]);

  const sync = useCallback(async (quiet = false) => {
    try {
      const r = await syncHrDocumentSigning(assignment.assignmentId);
      if (!quiet) setNotice(r.request ? REQUEST_LABEL[r.request.status] ?? r.request.status : 'Nothing to check yet.');
      load();
      if (r.request?.status === 'completed') { awaitingProvider.current = false; onChanged(); }
    } catch (e: any) {
      if (!quiet) setError(e.message || 'Could not check signing status.');
    }
  }, [assignment.assignmentId, load, onChanged]);

  // A signing request that is still open when the panel appears may have been finished in the provider's tab (or on another
  // device) since the last visit: check once automatically so the page never keeps offering Review & Sign for a form that is
  // already signed. One provider call per page view at most; nothing is recorded unless the server verifies it.
  const autoChecked = useRef(false);
  useEffect(() => {
    const open = status?.request && (status.request.status === 'prepared' || status.request.status === 'awaiting_signers');
    if (open && !autoChecked.current) { autoChecked.current = true; void sync(true); }
  }, [status, sync]);

  // When the person comes back from the signing tab, check once.
  useEffect(() => {
    function onFocus() { if (awaitingProvider.current) void sync(true); }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [sync]);

  async function begin(role: 'employee' | 'witness') {
    setBusy(role); setError(null); setNotice(null); setFallbackLink(null);
    // Open the tab synchronously (inside the click) so the browser does not treat it as a blocked popup.
    const tab = window.open('', '_blank');
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const { signLink } = await startHrDocumentSigning(assignment.assignmentId, role, returnUrl);
      awaitingProvider.current = true;
      if (tab) { tab.location.href = signLink; setNotice('The signing page opened in a new tab. When you have finished there, come back to this tab — it will check automatically.'); }
      else { setFallbackLink(signLink); setNotice('Your browser blocked the new tab. Use the link below to open the signing page.'); }
      load();
    } catch (e: any) {
      tab?.close();
      setError(e.message || 'Could not start signing.');
    } finally { setBusy(null); }
  }

  async function cancel() {
    setBusy('cancel'); setError(null);
    try {
      const r = await cancelHrDocumentSigning(assignment.assignmentId, cancelReason || undefined);
      setNotice(r.cancelled ? 'Signing request cancelled. The document itself is unchanged.' : 'There was no live signing request to cancel.');
      setCancelReason(''); load();
    } catch (e: any) { setError(e.message || 'Could not cancel the signing request.'); }
    finally { setBusy(null); }
  }

  if (assignment.status === 'cancelled' || assignment.status === 'reviewed_complete') return null;
  if (error && !status) return <div className="mt-2 text-xs text-red-700">{error}</div>;
  if (!status) return <div className="mt-2 text-xs text-gray-400">Checking electronic signing…</div>;

  const req = status.request;
  const live = !!req && (req.status === 'prepared' || req.status === 'awaiting_signers');
  const isHr = mode === 'admin';
  const callerIsNamedWitness = !!callerEmployeeId && assignment.equipmentCheckout?.witnessEmployeeId === callerEmployeeId;

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-white p-3 text-xs space-y-2" data-testid="signing-panel">
      {status.providerConfigured && !status.providerConnected && (
        <div role="note" className="rounded border-2 border-red-300 bg-red-50 text-red-800 px-2.5 py-2 font-semibold" data-testid="signing-not-connected">
          NOT CONNECTED — local preview only. Signing here is SIMULATED. It is not BoldSign and not a legal signature.
        </div>
      )}
      {status.providerConnected && status.providerEnvironment === 'sandbox' && (
        <div role="note" className="rounded border-2 border-amber-400 bg-amber-50 text-amber-900 px-2.5 py-2 font-semibold" data-testid="signing-sandbox">
          BoldSign SANDBOX — a real BoldSign test account. Your signature here is real in the test system but is NOT legally valid, and the signed copy is watermarked.
        </div>
      )}
      {/* Live is shown ONLY when the server says this request's environment is 'live' AND the provider is connected. Calm and neutral on
          purpose: no alarm colours, and no claim beyond what the status reports. HR review before completion is unchanged. */}
      {status.providerConnected && status.providerEnvironment === 'live' && (
        <div role="note" className="rounded border border-gray-200 bg-gray-50 text-gray-700 px-2.5 py-2" data-testid="signing-live">
          Live e-signature through BoldSign. HR reviews the signed copy before it is complete.
        </div>
      )}
      {/* A request on file keeps the environment it was created in. When the next request would use a different one, say so plainly. */}
      {!live && !!req && !!status.canStartEmployeeSigning && status.configuredEnvironment && status.configuredEnvironment !== status.providerEnvironment && (
        <div role="note" className="text-gray-600" data-testid="signing-next-environment">
          {status.configuredEnvironment === 'live'
            ? 'A new signing request will use Live e-signature through BoldSign. HR reviews the signed copy before it is complete.'
            : 'A new signing request will use the BoldSign SANDBOX test account. It is not legally valid.'}
        </div>
      )}
      {!status.providerConfigured && (
        <div className="text-gray-600" data-testid="signing-not-set-up">
          Electronic signing is not set up yet. Print the form, sign it on paper, and upload the signed copy instead.
        </div>
      )}

      {status.providerConfigured && (
        <>
          <div className="text-gray-700">
            <span className="font-semibold">E-signature:</span>{' '}
            {req ? (REQUEST_LABEL[req.status] ?? req.status) : 'Not started'}
            {req?.providerEnvironment && (
              <span className="text-gray-500" data-testid="signing-request-environment"> ({req.providerEnvironment === 'live' ? 'Live' : 'Sandbox'})</span>
            )}
            {req && req.status === 'awaiting_signers' && (
              <span className="text-gray-500"> — employee {req.employeeSigned ? 'signed' : 'not yet'}{req.witnessRequired ? `, witness ${req.witnessSigned ? 'signed' : 'not yet'}` : ''}</span>
            )}
            {req?.status === 'failed' && req.failureReason && <span className="text-gray-500"> ({req.failureReason})</span>}
          </div>
          {status.witnessRequired && <div className="text-gray-500">A witness will also sign on their own line.</div>}

          <div className="flex flex-wrap gap-2 items-center">
            {mode === 'self' && status.canStartEmployeeSigning && (
              <button type="button" disabled={busy !== null} onClick={() => begin('employee')}
                className="px-3 py-1.5 rounded-md bg-[#4AABDE] text-white font-medium disabled:opacity-50" data-testid="review-and-sign">
                {busy === 'employee' ? 'Opening…' : live && req?.employeeSigned ? 'Open signing page again' : 'Review & Sign'}
              </button>
            )}
            {(isHr || callerIsNamedWitness) && status.canSignAsWitness && (
              <button type="button" disabled={busy !== null} onClick={() => begin('witness')}
                className="px-3 py-1.5 rounded-md bg-[#4AABDE] text-white font-medium disabled:opacity-50" data-testid="sign-as-witness">
                {busy === 'witness' ? 'Opening…' : 'Sign as witness'}
              </button>
            )}
            {live && (
              <button type="button" disabled={busy !== null} onClick={() => sync(false)} className="text-[#4AABDE] hover:underline" data-testid="check-signing">
                Check signing status
              </button>
            )}
          </div>
          {fallbackLink && <a href={fallbackLink} target="_blank" rel="noopener noreferrer" className="text-[#4AABDE] underline">Open the signing page</a>}

          {isHr && live && (
            <div className="flex flex-wrap gap-2 items-center pt-1 border-t border-gray-100">
              <input type="text" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Reason (optional)" aria-label="Reason for cancelling the signing request"
                className="border border-gray-300 rounded px-2 py-1 text-xs w-44" />
              <button type="button" disabled={busy !== null} onClick={cancel} className="text-red-700 hover:underline" data-testid="cancel-signing">
                {busy === 'cancel' ? 'Cancelling…' : 'Cancel signing request'}
              </button>
            </div>
          )}
        </>
      )}
      {notice && <div className="text-gray-700" role="status">{notice}</div>}
      {error && <div className="text-red-700" role="alert">{error}</div>}
    </div>
  );
}
